// LLM generated - Model Claude 4 Sonnet
import { forwardRef } from 'react';
import { Box, Flex, Stack } from '@mantine/core';

import { Text } from '~/domains/design-system/Text';
import IconTagAurora from '~/domains/design-system/icons/IconTagAurora';
import IconRocketAurora from '~/domains/design-system/icons/IconRocketAurora';
import IconShieldAurora from '~/domains/design-system/icons/IconShieldAurora';
import IconCoinAurora from '~/domains/design-system/icons/IconCoinAurora';
import { BoostType } from '~/domains/promotions/types';
import dt from '~/testing';
import type { PromoCardProps } from '~/domains/promotions/types/carousel.types';

import { useStyles } from './PromoCard.styles';
import { usePromoCard } from './usePromoCard';

const getPromoIcon = (type: PromoCardProps['type'], boostType?: PromoCardProps['boostType']) => {
  switch (type) {
    case 'voucher':
      return <IconTagAurora size={32} />;
    case 'bonus':
      return <IconCoinAurora size={32} />;
    case 'boost':
      return boostType === BoostType.NO_SWEAT ? (
        <IconShieldAurora size={32} />
      ) : (
        <IconRocketAurora size={32} />
      );
    default:
      return null;
  }
};

/**
 * PromoCard displays promotional content like vouchers, boosts, and deposit bonuses in a consistent card format.
 *
 * Layout: gradient `value` text (or icon fallback when `value` is omitted), title + description,
 * an "Apply" / action pill on the right, and a floating expiration chip above the surface when
 * applicable. Variants adjust surface height, border, and theme (`default`, `compact`, `board`,
 * `entry-submission`).
 *
 * Action callbacks:
 * - `onAction`: full card clickable, overrides split actions
 * - `onActionLeft`: makes the left content clickable
 * - `onActionRight`: makes the right action area (pill or column) clickable
 *
 * Action text is auto-determined for vouchers (Enter/Apply) and respects `isSelected` to
 * render Remove. See `usePromoCard` for the business logic.
 */
export const PromoCard = forwardRef<HTMLDivElement, PromoCardProps>(
  (
    {
      type,
      boostType,
      voucherType,
      restrictedContestId,
      title,
      description,
      value,
      actionText,
      expirationDate,
      onAction,
      onActionLeft,
      onActionRight,
      disabled = false,
      isSelected = false,
      isApplied = false,
      variant = 'default',
      showBorder = false,
      isCompact = false,
      alwaysShowExpiration = false,
      className,
      ...props
    },
    ref
  ) => {
    const {
      displayActionText,
      expirationChipLabel,
      isDisabled,
      isClickable,
      isLeftClickable,
      isRightClickable,
      hasDeadlinePassed,
    } = usePromoCard({
      type,
      boostType,
      voucherType,
      restrictedContestId,
      actionText,
      expirationDate,
      disabled,
      isSelected,
      isApplied,
      alwaysShowExpiration,
      onAction,
      onActionLeft,
      onActionRight,
    });

    const { classes, cx } = useStyles({ variant, showBorder, isCompact });
    const isFat = variant === 'fat';
    const icon = getPromoIcon(type, boostType);

    const expirationChipClassName = cx(
      classes.expirationChip,
      hasDeadlinePassed && classes.expirationChipExpired
    );

    const handlePillClick = (e: React.MouseEvent) => {
      if (isRightClickable && onActionRight) {
        e.stopPropagation();
        onActionRight();
      }
    };

    return (
      <Box
        ref={ref}
        className={cx(classes.root, className)}
        style={props.style}
        data-test-id={dt.promotions.promoCard}
        {...props}
      >
        {(!isFat || alwaysShowExpiration) && expirationChipLabel && (
          <Box className={expirationChipClassName}>
            <Text variant="subhead-xx-small">{expirationChipLabel}</Text>
          </Box>
        )}

        <Box
          className={cx(
            classes.surface,
            isClickable && classes.clickable,
            isDisabled && classes.disabled
          )}
          onClick={isClickable ? onAction : undefined}
        >
          <Flex align="center" /* eslint-disable-line local-rules/use-theme-tokens */ gap={16} className={classes.content}>
            <Box
              className={cx(classes.leftSection, isLeftClickable && classes.textContentClickable)}
              onClick={isLeftClickable ? onActionLeft : undefined}
            >
              {value ? (
                <Text
                  tag="span"
                  className={cx([
                    classes.leftContainer,
                    classes.valueText,
                    value.length > 4 && classes.valueTextCompact,
                  ])}
                >
                  {value}
                </Text>
              ) : (
                <Box className={cx(classes.leftContainer, classes.iconContainer)}>{icon}</Box>
              )}
              <Stack className={classes.titleStack}>
                <Text variant="headline-small" className={classes.title}>
                  {title}
                </Text>
                <Text variant="subhead-x-small" className={classes.description}>
                  {description}
                </Text>
                {isFat && !alwaysShowExpiration && expirationChipLabel && (
                  <Text variant="subhead-x-small" className={classes.expirationText}>
                    {expirationChipLabel}
                  </Text>
                )}
              </Stack>
            </Box>

            {displayActionText && (
              <Box
                role={isRightClickable ? 'button' : undefined}
                tabIndex={isRightClickable ? 0 : undefined}
                className={cx(
                  classes.applyPill,
                  isRightClickable && classes.applyPillClickable,
                  isSelected && classes.applyPillRemove,
                  isApplied && classes.applyPillApplied
                )}
                onClick={handlePillClick}
              >
                <Text variant="headline-x-small">{displayActionText}</Text>
              </Box>
            )}
          </Flex>
        </Box>
      </Box>
    );
  }
);

PromoCard.displayName = 'PromoCard';
